// =============================================================================
// Settings — faithful port of POS frmSettings/frmTax + IMS Settings.
// Manages sales-tax rates (used by Sales Register & Estimates) and shows
// register/store config (read from Vite env).
// =============================================================================
import { useEffect, useState, useCallback } from 'react'
import { Plus, X, Monitor, Trash2 } from 'lucide-react'
import {
  getTaxRates, createTaxRate, updateTaxRate,
  listRegisters, createRegister, updateRegister, deleteRegister,
  getSetting, setSetting,
} from '../services/data'
import { DEFAULT_GUARANTEE } from '../lib/reports'
import {
  getDeviceRegister, setDeviceRegister, clearDeviceRegister,
  type DeviceRegisterProfile,
} from '../services/register'
import { PageHeader, Card, DataTable, Spinner, Button } from '../components/shared/ui'
import type { TaxRate, Register } from '../types'
import { toast } from 'react-toastify'

export default function Settings() {
  const [rates, setRates] = useState<TaxRate[]>([])
  const [registers, setRegisters] = useState<Register[]>([])
  const [loading, setLoading] = useState(true)
  const [edit, setEdit] = useState<TaxRate | 'new' | null>(null)
  const [regEdit, setRegEdit] = useState<Register | 'new' | null>(null)
  const [device, setDevice] = useState<DeviceRegisterProfile | null>(getDeviceRegister())
  const [quoteText, setQuoteText] = useState('')
  const [savingQuote, setSavingQuote] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [r, regs, qt] = await Promise.all([getTaxRates(), listRegisters(), getSetting('estimate_quote_text')])
      setRates(r); setRegisters(regs); setQuoteText(qt ?? '')
    } finally { setLoading(false) }
  }, [])

  const saveQuote = async () => {
    setSavingQuote(true)
    try {
      await setSetting('estimate_quote_text', quoteText.trim())
      toast.success('Estimate quote text saved')
    } catch { toast.error('Failed to save quote text') } finally { setSavingQuote(false) }
  }

  useEffect(() => { load() }, [load])

  const removeRegister = async (r: Register) => {
    if (!window.confirm(`Delete Register ${r.register_number}? Devices signed into it will need to re-select.`)) return
    try { await deleteRegister(r.id); toast.success('Register deleted'); load() }
    catch { toast.error('Failed to delete register') }
  }

  const signInHere = (r: Register) => {
    const cur = getDeviceRegister()
    const p: DeviceRegisterProfile = {
      registerNumber: r.register_number,
      registerName: r.name,
      printerType: cur?.printerType ?? 'serial',
      drawerType: cur?.drawerType ?? 'serial',
    }
    setDeviceRegister(p); setDevice(p)
    toast.success(`This device is now Register ${r.register_number}`)
  }

  const makeDefault = async (r: TaxRate) => {
    try {
      // Clear existing default, set new one.
      await Promise.all(rates.filter(x => x.is_default).map(x => updateTaxRate(x.id, { is_default: false })))
      await updateTaxRate(r.id, { is_default: true })
      toast.success('Default tax rate updated'); load()
    } catch { toast.error('Failed to update default') }
  }

  return (
    <div>
      <PageHeader title="Settings" subtitle="Tax rates & store configuration" />

      <Card className="p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800">Sales Tax Rates</h3>
          <Button onClick={() => setEdit('new')}><Plus size={16} /> Add Rate</Button>
        </div>
        {loading ? <Spinner label="Loading tax rates…" /> : (
          <DataTable
            rows={rates}
            onRowClick={r => setEdit(r)}
            emptyMessage="No tax rates configured"
            columns={[
              { key: 'county', header: 'County', render: r => r.county || '—' },
              { key: 'state', header: 'State', render: r => r.state || '—' },
              { key: 'rate', header: 'Rate %', render: r => `${r.rate}%`, className: 'text-right' },
              { key: 'is_default', header: 'Default', render: r => r.is_default
                ? <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs">Default</span>
                : <button onClick={e => { e.stopPropagation(); makeDefault(r) }} className="text-xs text-green-700 hover:underline">Set default</button> },
            ]}
          />
        )}
      </Card>

      {/* This device — which register lane this machine acts as (localStorage) */}
      <Card className="p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Monitor size={18} className="text-green-700" />
          <h3 className="font-semibold text-gray-800">This Device</h3>
        </div>
        {device ? (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm text-gray-700">
              <p>Signed in as <span className="font-semibold">Register {device.registerNumber}</span>{device.registerName ? ` · ${device.registerName}` : ''}</p>
              <p className="text-xs text-gray-500 mt-0.5">Printer: {device.printerType} · Cash drawer: {device.drawerType}</p>
            </div>
            <Button variant="secondary" onClick={() => { clearDeviceRegister(); setDevice(null); toast.info('Signed out of register on this device') }}>Sign out</Button>
          </div>
        ) : (
          <p className="text-sm text-gray-500">This device is not signed into a register. Use <span className="font-medium">Use on this device</span> below, or the register chip on the Sales Register.</p>
        )}
      </Card>

      {/* Registers — DB-backed lanes; Stripe reader linked to each register # */}
      <Card className="p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800">Registers</h3>
          <Button onClick={() => setRegEdit('new')}><Plus size={16} /> Add Register</Button>
        </div>
        {loading ? <Spinner label="Loading registers…" /> : (
          <DataTable
            rows={registers}
            onRowClick={r => setRegEdit(r)}
            emptyMessage="No registers configured — add one so terminals can sign in."
            columns={[
              { key: 'register_number', header: 'Reg #', render: r => `#${r.register_number}` },
              { key: 'name', header: 'Name', render: r => r.name || '—' },
              { key: 'stripe_reader_id', header: 'Stripe Reader', render: r => r.stripe_reader_id || <span className="text-gray-400">none</span> },
              { key: 'stripe_location_id', header: 'Location', render: r => r.stripe_location_id || '—' },
              { key: 'is_active', header: 'Active', render: r => r.is_active ? <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs">Active</span> : <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-500 text-xs">Inactive</span> },
              { key: 'use', header: '', render: r => (
                <div className="flex items-center gap-2 justify-end">
                  <button onClick={e => { e.stopPropagation(); signInHere(r) }} className="text-xs text-green-700 hover:underline">Use on this device</button>
                  <button onClick={e => { e.stopPropagation(); removeRegister(r) }} className="text-gray-400 hover:text-red-600"><Trash2 size={15} /></button>
                </div>
              ) },
            ]}
          />
        )}
      </Card>

      {/* Estimate quote / guarantee text — legacy frmEstimateHeader (tblUtility.QuoteText) */}
      <Card className="p-5 mb-6">
        <h3 className="font-semibold text-gray-800 mb-1">Estimate Quote / Guarantee Text</h3>
        <p className="text-xs text-gray-500 mb-3">Printed in the footer of every estimate. Leave blank to use the default guarantee wording.</p>
        <textarea
          value={quoteText}
          onChange={e => setQuoteText(e.target.value)}
          rows={4}
          placeholder={DEFAULT_GUARANTEE}
          className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
        />
        <div className="flex items-center justify-between mt-2">
          <button onClick={() => setQuoteText('')} className="text-xs text-gray-500 hover:underline">Reset to default wording</button>
          <Button onClick={saveQuote} disabled={savingQuote}>{savingQuote ? 'Saving…' : 'Save Quote Text'}</Button>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="font-semibold text-gray-800 mb-3">Defaults (environment)</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div><p className="text-gray-500">App Name</p><p className="font-medium">{import.meta.env.VITE_APP_NAME ?? 'Thomsons Web'}</p></div>
          <div><p className="text-gray-500">Default Printer</p><p className="font-medium">{import.meta.env.VITE_PRINTER_TYPE ?? '—'}</p></div>
          <div><p className="text-gray-500">Default Cash Drawer</p><p className="font-medium">{import.meta.env.VITE_CASH_DRAWER_TYPE ?? '—'}</p></div>
        </div>
        <p className="text-xs text-gray-400 mt-3">Per-device printer/drawer and register identity are set above and saved locally; the Stripe reader is taken from the selected register's record. These env values are only fallback defaults.</p>
      </Card>

      {edit && <TaxRateForm rate={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load() }} />}
      {regEdit && <RegisterForm register={regEdit === 'new' ? null : regEdit} onClose={() => setRegEdit(null)} onSaved={() => { setRegEdit(null); load() }} />}
    </div>
  )
}

function RegisterForm({ register, onClose, onSaved }: { register: Register | null; onClose: () => void; onSaved: () => void }) {
  const [number, setNumber] = useState(register?.register_number != null ? String(register.register_number) : '')
  const [name, setName] = useState(register?.name ?? '')
  const [readerId, setReaderId] = useState(register?.stripe_reader_id ?? '')
  const [locationId, setLocationId] = useState(register?.stripe_location_id ?? '')
  const [isActive, setIsActive] = useState(register?.is_active ?? true)
  const [saving, setSaving] = useState(false)

  const save = async () => {
    const n = parseInt(number, 10)
    if (isNaN(n)) { toast.error('Enter a valid register number'); return }
    setSaving(true)
    try {
      const payload = {
        register_number: n,
        name: name.trim() || null,
        stripe_reader_id: readerId.trim() || null,
        stripe_location_id: locationId.trim() || null,
        is_active: isActive,
      }
      if (register) await updateRegister(register.id, payload)
      else await createRegister(payload)
      toast.success('Register saved'); onSaved()
    } catch (e) {
      console.error(e)
      toast.error('Failed to save register (number may already exist)')
    } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg">{register ? `Edit Register ${register.register_number}` : 'New Register'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500">Register Number</label>
              <input type="number" value={number} onChange={e => setNumber(e.target.value)} disabled={!!register}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Name</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Front Counter"
                className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Stripe Reader ID</label>
            <input value={readerId} onChange={e => setReaderId(e.target.value)} placeholder="tmr_xxx / wisepad id"
              className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            <p className="text-xs text-gray-400 mt-1">When set, card charges from this register connect to this specific reader.</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Stripe Location ID (optional)</label>
            <input value={locationId} onChange={e => setLocationId(e.target.value)} placeholder="tml_xxx"
              className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} /> Active (available for sign-in)
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Close</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </Card>
    </div>
  )
}

function TaxRateForm({ rate, onClose, onSaved }: { rate: TaxRate | null; onClose: () => void; onSaved: () => void }) {
  const [county, setCounty] = useState(rate?.county ?? '')
  const [state, setState] = useState(rate?.state ?? '')
  const [pct, setPct] = useState(rate?.rate != null ? String(rate.rate) : '')
  const [isDefault, setIsDefault] = useState(rate?.is_default ?? false)
  const [saving, setSaving] = useState(false)

  const save = async () => {
    const r = parseFloat(pct)
    if (isNaN(r)) { toast.error('Enter a valid rate'); return }
    setSaving(true)
    try {
      const payload = { county: county || null, state: state || null, rate: r, is_default: isDefault }
      if (rate) await updateTaxRate(rate.id, payload)
      else await createTaxRate(payload)
      toast.success('Tax rate saved'); onSaved()
    } catch (e) { console.error(e); toast.error('Failed to save tax rate') } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg">{rate ? 'Edit Tax Rate' : 'New Tax Rate'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500">County</label>
              <input value={county} onChange={e => setCounty(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">State</label>
              <input value={state} onChange={e => setState(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Rate %</label>
            <input type="number" value={pct} onChange={e => setPct(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)} /> Default rate (used by Sales Register)
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Close</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </Card>
    </div>
  )
}
